/**
 * Constitution API — typed wrappers + TanStack Query keys.
 *
 * Mirrors apps/chaos-gateway/src/routes/constitution.ts. The gateway already
 * translates snake_case → camelCase so this client surfaces idiomatic TS
 * shapes from @chaos/types/constitution.
 *
 * All mutations are role-gated server-side (founder + admin; constitution
 * is founder-only). The UI also reads the auth-store role to hide affordances
 * the user wouldn't be allowed to use, but the backend is the source of truth.
 */
import type {
  ChangeProposalsList,
  ConstitutionDoc,
  ConstitutionRulesList,
  Goal,
  GoalPatch,
  GoalProposal,
  GoalsList,
  Mission,
  MissionProposal,
  PrincipleProposal,
  PrinciplesList,
  ProposalAck,
  StrategiesList,
  StrategyProposal,
} from '@chaos/types/constitution';

import { apiJson } from './client';

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export interface ListGoalsParams {
  status?: 'open' | 'in_progress' | 'blocked' | 'done' | 'dropped';
  strategyId?: string;
  /** Filter by parent goal id. Pass `null` for top-level goals only. */
  parentId?: string | null;
}

export interface ListProposalsParams {
  status?: 'pending' | 'decided';
}

export interface ListStrategiesParams {
  status?: 'draft' | 'approved' | 'active' | 'archived' | 'retired';
  period?: string;
  scope?: string;
}

export const constitutionApi = {
  // ── Constitution ───────────────────────────────────────────────────────
  getActive(scope: string = 'global'): Promise<ConstitutionDoc | null> {
    return apiJson<ConstitutionDoc | null>(`/constitution/active${qs({ scope })}`);
  },
  listRules(scope: string = 'global'): Promise<ConstitutionRulesList> {
    return apiJson<ConstitutionRulesList>(`/constitution/rules${qs({ scope })}`);
  },
  submitProposal(
    input: import('@chaos/types/constitution').ConstitutionRuleProposal,
  ): Promise<ProposalAck> {
    return apiJson<ProposalAck>('/constitution/proposals', { method: 'POST', json: input });
  },

  // ── Mission ────────────────────────────────────────────────────────────
  getMission(scope: string = 'global'): Promise<Mission | null> {
    return apiJson<Mission | null>(`/mission${qs({ scope })}`);
  },
  proposeMission(input: MissionProposal): Promise<ProposalAck> {
    return apiJson<ProposalAck>('/mission', { method: 'PUT', json: input });
  },

  // ── Principles ─────────────────────────────────────────────────────────
  listPrinciples(scope: string = 'general'): Promise<PrinciplesList> {
    return apiJson<PrinciplesList>(`/principles${qs({ scope })}`);
  },
  proposePrinciple(input: PrincipleProposal): Promise<ProposalAck> {
    return apiJson<ProposalAck>('/principles', { method: 'POST', json: input });
  },

  // ── Strategies ─────────────────────────────────────────────────────────
  listStrategies(params: ListStrategiesParams = {}): Promise<StrategiesList> {
    return apiJson<StrategiesList>(
      `/strategies${qs(params as Record<string, string | number | undefined>)}`,
    );
  },
  proposeStrategy(input: StrategyProposal): Promise<ProposalAck> {
    return apiJson<ProposalAck>('/strategies', { method: 'POST', json: input });
  },

  // ── Goals ──────────────────────────────────────────────────────────────
  listGoals(params: ListGoalsParams = {}): Promise<GoalsList> {
    const raw: Record<string, string | number | undefined> = {};
    if (params.status) raw.status = params.status;
    if (params.strategyId) raw.strategyId = params.strategyId;
    if (params.parentId === null) raw.parentId = 'null';
    else if (params.parentId) raw.parentId = params.parentId;
    return apiJson<GoalsList>(`/goals${qs(raw)}`);
  },
  getGoal(goalId: string): Promise<Goal> {
    return apiJson<Goal>(`/goals/${encodeURIComponent(goalId)}`);
  },
  listGoalChildren(goalId: string): Promise<GoalsList> {
    return apiJson<GoalsList>(`/goals/${encodeURIComponent(goalId)}/children`);
  },
  createGoal(input: GoalProposal): Promise<Goal> {
    return apiJson<Goal>('/goals', { method: 'POST', json: input });
  },
  patchGoal(goalId: string, input: GoalPatch): Promise<Goal> {
    return apiJson<Goal>(`/goals/${encodeURIComponent(goalId)}`, { method: 'PATCH', json: input });
  },
  deleteGoal(goalId: string): Promise<{ ok: boolean; id: string }> {
    return apiJson<{ ok: boolean; id: string }>(`/goals/${encodeURIComponent(goalId)}`, {
      method: 'DELETE',
    });
  },

  // ── Proposals ──────────────────────────────────────────────────────────
  listProposals(params: ListProposalsParams = {}): Promise<ChangeProposalsList> {
    return apiJson<ChangeProposalsList>(
      `/constitution/proposals${qs(params as Record<string, string | number | undefined>)}`,
    );
  },
  getProposal(proposalId: string): Promise<import('@chaos/types/constitution').ChangeProposal> {
    return apiJson<import('@chaos/types/constitution').ChangeProposal>(
      `/constitution/proposals/${encodeURIComponent(proposalId)}`,
    );
  },
  approveProposal(
    proposalId: string,
    notes: string = '',
  ): Promise<{ ok: boolean; proposalId: string; kind: string; decision: 'approve' }> {
    return apiJson(`/constitution/proposals/${encodeURIComponent(proposalId)}/approve`, {
      method: 'POST',
      json: { notes },
    });
  },
  rejectProposal(
    proposalId: string,
    notes: string = '',
  ): Promise<{ ok: boolean; proposalId: string; kind: string; decision: 'reject' }> {
    return apiJson(`/constitution/proposals/${encodeURIComponent(proposalId)}/reject`, {
      method: 'POST',
      json: { notes },
    });
  },
};

export const constitutionKeys = {
  all: ['constitution'] as const,

  active: (scope: string) => [...constitutionKeys.all, 'active', scope] as const,
  rules: (scope: string) => [...constitutionKeys.all, 'rules', scope] as const,

  mission: (scope: string) => [...constitutionKeys.all, 'mission', scope] as const,

  principles: (scope: string) => [...constitutionKeys.all, 'principles', scope] as const,

  strategies: (params: ListStrategiesParams) =>
    [...constitutionKeys.all, 'strategies', params] as const,

  goals: (params: ListGoalsParams) => [...constitutionKeys.all, 'goals', params] as const,
  goal: (goalId: string) => [...constitutionKeys.all, 'goal', goalId] as const,
  goalChildren: (goalId: string) => [...constitutionKeys.all, 'goal', goalId, 'children'] as const,

  proposals: (params: ListProposalsParams) =>
    [...constitutionKeys.all, 'proposals', params] as const,
  proposal: (proposalId: string) => [...constitutionKeys.all, 'proposal', proposalId] as const,
};
